<template>
  <div>
    <mia-fade-transition mode="out-in">
      <mia-wait for="loading debts">
        <template #waiting>
          <div class="flex items-center justify-center py-8">
            <mia-wave-loader />
          </div>
        </template>

        <template #default>
          <mia-form
            key="form"
            @submit.prevent
          >
            <mia-form-section
              title="Forderungen"
              vertical
            >
              <slot name="before" />

              <mia-has-feature feature="customer_portal">
                <mia-notice
                  permanent
                  type="warning"
                  class="mb-6"
                >
                  <b>Achtung!</b> Forderungen mit dem Debitor <strong>"Kunde"</strong> können im Kundenportal vom Kunden eingesehen werden!
                </mia-notice>
              </mia-has-feature>

              <datatable
                v-if="hasDebts"
                class="w-full table-auto datatable"
                :data="debtsCombined"
                :columns="columns"
              >
                <template #footer>
                  <tr>
                    <td
                      colspan="5"
                      class="text-right"
                    >
                      Summe Forderungen
                    </td>
                    <td class="text-right tabular-nums">
                      {{ stats.sum | price('EUR') }}
                    </td>
                    <td colspan="2" />
                  </tr>
                  <tr>
                    <td
                      colspan="5"
                      class="text-right"
                    >
                      Summe Zahlungen
                    </td>
                    <td class="text-right tabular-nums">
                      {{ stats.sumPaid | price('EUR') }}
                    </td>
                    <td colspan="2" />
                  </tr>
                  <tr>
                    <td
                      colspan="5"
                      class="text-right"
                    >
                      Differenz
                    </td>
                    <td
                      class="font-bold text-right tabular-nums"
                      :class="{
                        'text-red-700': (stats.sum - stats.sumPaid) > 0,
                        'text-blue-700': ((stats.sum - stats.sumPaid) < 0)
                      }"
                    >
                      {{ stats.difference | price('EUR') }}
                    </td>
                    <td colspan="2" />
                  </tr>
                </template>
              </datatable>
              <mia-notice
                v-else
                permanent
              >
                Für dieses Fahrzeug wurden noch keine Forderungen angelegt
              </mia-notice>
            </mia-form-section>
            <template #actions>
              <div class="flex items-center justify-between flex-1">
                <div>
                  <div>
                    <span
                      v-if="car.item_opos_finished_at"
                      class="italic"
                    >OPOS wurden am {{ car.item_opos_finished_at | datetime }} Uhr abgeschlossen.</span>
                    <mia-is-allowed
                      v-else
                      to="finish_opos"
                    >
                      <mia-submit-button
                        :disabled="$wait.any"
                        label="OPOS abgeschlossen"
                        @click.prevent="finishOpos"
                      />
                    </mia-is-allowed>
                  </div>
                  <mia-is-allowed
                    v-if="car.item_opos_finished_at && car.business_model === 'agency' && car.item_maker_payment_method === 'cash'"
                    to="finish_opos"
                  >
                    <div class="mt-4">
                      <label class="flex items-center mb-2 font-normal cursor-pointer">
                        <mia-checkbox
                          class="w-5 h-5 shadow-xs cursor-pointer"
                          :checked="car.item_maker_cash_payment_received === 'Y'"
                          @input="onMakerCashPaymentReceivedInput"
                        />
                        <span class="ml-2">Rechnung beim Hersteller bezahlt</span>
                      </label>
                    </div>
                  </mia-is-allowed>
                </div>
                <div>
                  <mia-submit-button
                    :disabled="$wait.any"
                    :label="hasDebts ? 'Weitere Forderungen buchen' : 'Forderung buchen'"
                    @click.prevent="addDebt"
                  />
                  <mia-submit-button
                    :disabled="$wait.any"
                    class="ml-4"
                    :label="hasDebts ? 'Weitere Zahlungen buchen' : 'Zahlung buchen'"
                    @click.prevent="addPaidDebt"
                  />
                </div>
              </div>
            </template>
          </mia-form>
        </template>
      </mia-wait>
    </mia-fade-transition>

    <mia-opos-agency-business-modal
      v-if="showOposAgencyBusinessModal"
      :show="showOposAgencyBusinessModal"
      @cancel="showOposAgencyBusinessModal = false"
      @closed="showOposAgencyBusinessModal = false"
      @submit="onOposAgencyBusinessModalSubmit"
    />
  </div>
</template>

<script>
import FormSection from '@/components/ui/widgets/FormSection.vue'
import Form from '@/components/ui/widgets/Form.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import { getCar } from '@/api/car'
import { finishOpos, getDebts, updateMakerCashPaymentReceived } from '@/api/cars'
import Notice from '@/components/cars/edit/Notice.vue'
import DateTimeColumn from '@/components/ui/datatable/columns/DateTimeColumn.vue'
import PriceColumn from '@/components/ui/datatable/columns/PriceColumn.vue'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import OposAgencyBusinessModal from '@/components/cars/OposAgencyBusinessModal.vue'
import { toYNBool } from '@/helpers/utils'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import { isEmpty } from 'lodash'

export default {
  name: 'MiaCarsEditOposDebtsForm',

  components: {
    'mia-form': Form,
    'mia-form-section': FormSection,
    'mia-submit-button': SubmitButton,
    'mia-notice': Notice,
    'mia-opos-agency-business-modal': OposAgencyBusinessModal
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      debts: [],
      debtsPaid: [],
      stats: {
        sum: 0,
        sumPaid: 0,
        difference: 0
      },
      showOposAgencyBusinessModal: false,
      oposAgencyBusinessData: {}
    }
  },

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    columns() {
      return [
        {
          field: 'item_date',
          label: 'Datum',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            displayFormat: 'dd.MM.yyyy'
          }
        },
        {
          field: '',
          label: 'Typ',
          headerAlign: 'left',
          sortable: false,
          representedAs: row => (this.debts.includes(row) ? 'Forderung' : 'Zahlung')
        },
        {
          field: 'item_invoice_id',
          label: 'Rechnungsnummer',
          headerAlign: 'left'
        },
        {
          field: 'item_debtor_title',
          label: 'Debitor',
          headerAlign: 'left',
          representedAs: row => row.item_debtor_title || 'Kunde'
        },
        {
          field: 'item_assigned_customer_id',
          label: 'Kunde',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/customers/:item_assigned_customer_id',
            external: true,
            emptyLabel: '<span class="italic">ohne Kundenzuordnung</span>',
            labelProp: (row) => {
              if (parseInt(row.item_debtor, 10) !== 0 || !row.item_assigned_customer_id) {
                return ''
              }

              return this.getCustomerName(row)
            }
          }
        },
        {
          field: 'item_value',
          label: 'Bruttosumme',
          align: 'right',
          headerAlign: 'left',
          component: PriceColumn,
          class: 'tabular-nums',
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR',
            class: row => (this.debts.includes(row) ? 'text-red-700' : '')
          }
        },
        {
          label: '',
          field: '',
          headerAlign: 'left',
          align: 'right',
          component: ActionButtonColumn,
          meta: {
            actions: [
              ...(this.authDealer.item_is_allowed_car_invoices ? [
                {
                  icon: 'office-file-pdf',
                  class: 'border-orange-mia text-orange-mia hover:bg-orange-mia',
                  attrs: {
                    title: 'Rechnung in neuem Tab öffnen'
                  },
                  isVisible: row => this.debts.includes(row) && row.item_file_id !== null,
                  onClick: (row) => {
                    window.open(row.item_file_url)
                  }
                }
              ] : []),
              {
                icon: 'pencil-write',
                onClick: (row) => {
                  if (this.debts.includes(row)) {
                    mia.car.debts.edit(row.id, () => {
                      this.loadDebts(this.car.id)
                    })
                  } else {
                    mia.car.debts.paid.edit(row.id, () => {
                      this.loadDebts(this.car.id)
                    })
                  }
                }
              },
              {
                icon: 'bin-1',
                class: 'border-red-500 text-red-500 hover:bg-red-500',
                onClick: (row) => {
                  if (this.debts.includes(row)) {
                    mia.car.debts.remove.confirm(row.id, row.item_invoice_id, () => {
                      this.loadDebts(this.car.id)
                    })
                  } else {
                    mia.car.debts.paid.remove.confirm(row.id, row.item_value, () => {
                      this.loadDebts(this.car.id)
                    })
                  }
                }
              }
            ]
          }
        }
      ]
    },

    hasDebts() {
      return this.debts.length > 0 || this.debtsPaid.length > 0
    },

    debtsCombined() {
      return this.debts.concat(this.debtsPaid)
    },

    hasOposAgencyBusinessData() {
      return Object.keys(this.oposAgencyBusinessData).length > 0
    }
  },

  watch: {
    car: {
      immediate: true,
      deep: true,
      handler(car, oldCar) {
        if (!oldCar || parseInt(car.id, 10) !== parseInt(oldCar.id, 10)) {
          this.loadDebts(car.id)
        }
      }
    }
  },

  methods: {
    async loadDebts(carId) {
      this.$wait.start('loading debts')
      const [error, data] = await getDebts(carId)
      this.$wait.end('loading debts')

      if (error) return

      this.debts = data.debts
      this.debtsPaid = data.debtsPaid
      this.stats = data.stats
    },

    async finishOpos() {
      if (this.car.business_model === 'agency' && !this.hasOposAgencyBusinessData) {
        this.showOposAgencyBusinessModal = true
        return
      }

      this.$wait.start('finishing opos')
      const [errors] = await finishOpos(this.car.id, this.oposAgencyBusinessData)
      this.$wait.end('finishing opos')

      if (errors) {
        console.error(errors)
        window.eventBus.$emit('mia-error', {
          error: ['OPOS konnten nicht abgeschlossen werden.']
        })
        return
      }

      this.oposAgencyBusinessData = {}
      this.car = await getCar({
        carId: this.car.id
      })

      window.eventBus.$emit('mia-info', {
        info: ['OPOS erfolgreich abgeschlossen']
      })
    },

    addDebt() {
      this.$router.push({
        path: `/legacy/car_debts/${this.car.id}`,
        query: {
          return_to: encodeURIComponent(window.location.pathname)
        }
      })
    },

    addPaidDebt() {
      this.$router.push({
        path: `/legacy/car_debts_paid/${this.car.id}`,
        query: {
          return_to: encodeURIComponent(window.location.pathname)
        }
      })
    },

    onOposAgencyBusinessModalSubmit(event) {
      this.oposAgencyBusinessData = event
      this.showOposAgencyBusinessModal = false
      this.finishOpos()
    },

    async onMakerCashPaymentReceivedInput(event) {
      this.$wait.start('updating maker cash payment received')
      const [errors] = await updateMakerCashPaymentReceived(this.car.id, { item_maker_cash_payment_received: toYNBool(event.target.checked) })
      this.$wait.end('updating maker cash payment received')

      if (errors) {
        console.error(errors)
        window.eventBus.$emit('mia-error', {
          error: ['Daten konnten nicht gespeichert werden.']
        })
        return
      }

      this.car = await getCar({
        carId: this.car.id
      })

      window.eventBus.$emit('mia-info', {
        info: ['Die Zahlungsinformationen wurden erfolgreich aktualisiert']
      })
    },

    getCustomerName(debt) {
      const isCompany = !!debt.item_customer_company
      const title = isCompany && debt.item_customer_title ? `${debt.item_customer_title} ` : ''
      const customerName = [debt.item_customer_firstname, debt.item_customer_name]
        .filter(value => !isEmpty(value))
        .map(value => value.trim())
        .join(' ')

      if (isCompany) {
        return `Firma ${debt.item_customer_company.trim()}, ${title}${customerName}`.trim().replace(/,$/, '')
      }

      return `${title}${customerName}`.trim().replace(/,$/, '')
    }
  }
}
</script>

<style scoped>
::v-deep .datatable thead th {
  white-space: nowrap;
}

::v-deep .datatable tfoot tr:first-child > td {
  border-top-width: 3px;
  border-top-color: theme('colors.gray.500');
}
</style>
